/**
 * A tiny indirection that lets shared code call `analytics.track(...)` without
 * importing any analytics SDK. Each app plugs in its own implementation at
 * startup via `setAnalyticsPort`:
 *   - mobile registers a Mixpanel adapter (apps/mobile/lib/analytics), so calls
 *     reach Mixpanel;
 *   - web registers nothing (it tracks through its own apps/web/src/events
 *     layer), so on web every call here is a harmless no-op.
 * Net effect: shared code fires events on mobile and stays silent on web,
 * with no vendor SDK leaking into this package.
 */
// Registering the real adapter is async on mobile (the native SDK has to
// initialize first), so calls made in the window before registration — e.g.
// identify() from fetchProfile racing app bootstrap — are buffered in order
// and replayed on registration instead of being dropped. Capped so an app
// that never registers a port cannot accumulate calls forever.
const MAX_BUFFERED_CALLS = 50;
let activePort = null;
let buffered = [];
export const setAnalyticsPort = (port) => {
    activePort = port;
    const pending = buffered;
    buffered = [];
    for (const call of pending) {
        if (call.kind === "track")
            port.track(call.event, call.props);
        else if (call.kind === "identify")
            port.identify(call.distinctId);
        else
            port.reset();
    }
};
/** Test helper: return to the unregistered state with an empty buffer. */
export const resetAnalyticsPort = () => {
    activePort = null;
    buffered = [];
};
const enqueue = (call) => {
    if (buffered.length >= MAX_BUFFERED_CALLS)
        return;
    buffered.push(call);
};
export const analytics = {
    track: (event, props) => activePort
        ? activePort.track(event, props)
        : enqueue({ kind: "track", event, props }),
    identify: (distinctId) => activePort
        ? activePort.identify(distinctId)
        : enqueue({ kind: "identify", distinctId }),
    reset: () => (activePort ? activePort.reset() : enqueue({ kind: "reset" })),
};
//# sourceMappingURL=port.js.map